import { useEffect, useMemo, useRef, useState, type Key } from 'react';
import type { AssetLogListItem } from '@evimo/types/api/goldenRecord/find-asset-logs/find-asset-logs.response.dto';
import type { UseAssetLogsResult } from '~/app/hooks/assets/useAssetLogs';
import { useLogsInfiniteScroll } from '~/app/hooks/assets/useLogsInfiniteScroll';
import { filterAssetLogsByQuery } from '~/app/components/asset-detail/AssetLogsTable/utils';

type Options = {
  assetLogs: UseAssetLogsResult;
};

export function useAssetLogsTableState({ assetLogs }: Options) {
  const [selectedLog, setSelectedLog] = useState<AssetLogListItem | null>(null);
  const [searchValue, setSearchValue] = useState('');
  const [expandedRowKeys, setExpandedRowKeys] = useState<string[]>([]);

  const normalizedQuery = searchValue.trim().toLowerCase();
  const isClientFiltering = normalizedQuery.length > 0;

  useEffect(() => {
    setExpandedRowKeys([]);
  }, [assetLogs.filters]);

  const tableWrapRef = useRef<HTMLDivElement | null>(null);

  useLogsInfiniteScroll({
    enabled: !assetLogs.isLoading && !assetLogs.isError && !isClientFiltering,
    hasNextPage: assetLogs.hasNextPage,
    isFetchingNextPage: assetLogs.isFetchingNextPage,
    isFetchNextPageError: assetLogs.isFetchNextPageError,
    onLoadMore: assetLogs.fetchNextPage,
    loadedItemCount: assetLogs.logs.length,
    tableWrapRef,
  });

  const filteredLogs = useMemo(
    () => filterAssetLogsByQuery(assetLogs.logs ?? [], normalizedQuery),
    [assetLogs.logs, normalizedQuery],
  );

  const showInfiniteFooter =
    !isClientFiltering &&
    (assetLogs.hasNextPage ||
      assetLogs.isFetchingNextPage ||
      assetLogs.isFetchNextPageError);

  const toggleExpandedRow = (logId: string) => {
    setExpandedRowKeys((current) => (current.includes(logId) ? [] : [logId]));
  };

  const setExpandedFromKeys = (keys: readonly Key[]) => {
    const next = keys.map(String);
    setExpandedRowKeys(next.length > 0 ? [next[next.length - 1]] : []);
  };

  const resetFiltersAndSearch = () => {
    assetLogs.setFilters({});
    setSearchValue('');
  };

  return {
    selectedLog,
    setSelectedLog,
    searchValue,
    setSearchValue,
    expandedRowKeys,
    toggleExpandedRow,
    setExpandedFromKeys,
    filteredLogs,
    isClientFiltering,
    tableWrapRef,
    showInfiniteFooter,
    resetFiltersAndSearch,
  };
}
